import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const ROLES = [
  { value: "coach", label: "Coach" },
  { value: "trainer", label: "Athletic Trainer" },
  { value: "referee", label: "Referee/Official" },
  { value: "volunteer", label: "Volunteer" },
];

export default function AddStaffDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", role: "coach", team: "", phone: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      const { data } = await api.post("/staff", payload);
      toast.success(`${data.name} added to your roster`);
      setForm({ name: "", email: "", role: "coach", team: "", phone: "" });
      onCreated?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error("Failed to add staff");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-slate-900 border-white/10 text-slate-50 max-w-md"
        data-testid="add-staff-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Add Staff Member</DialogTitle>
          <DialogDescription className="text-slate-400">
            Register a coach, trainer, referee, or volunteer to your organization.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="font-label text-slate-400">FULL NAME *</Label>
            <Input
              data-testid="staff-name-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="bg-slate-950 border-white/10 focus:border-orange-500 mt-1"
              placeholder="e.g. Alex Morgan"
              required
            />
          </div>
          <div>
            <Label className="font-label text-slate-400">EMAIL</Label>
            <Input
              data-testid="staff-email-input"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="bg-slate-950 border-white/10 focus:border-orange-500 mt-1"
              placeholder="alex@school.edu"
            />
          </div>
          <div>
            <Label className="font-label text-slate-400">ROLE *</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="staff-role-select" className="bg-slate-950 border-white/10 mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-white/10 text-slate-100">
                {ROLES.map((r) => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="font-label text-slate-400">TEAM / DEPT</Label>
              <Input
                value={form.team}
                onChange={(e) => setForm({ ...form, team: e.target.value })}
                className="bg-slate-950 border-white/10 focus:border-orange-500 mt-1"
                placeholder="Varsity Basketball"
              />
            </div>
            <div>
              <Label className="font-label text-slate-400">PHONE</Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="bg-slate-950 border-white/10 focus:border-orange-500 mt-1"
                placeholder="(555) 123-4567"
              />
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-300">
              Cancel
            </Button>
            <Button
              data-testid="submit-staff-button"
              type="submit"
              disabled={loading}
              className="bg-orange-500 text-white hover:bg-orange-400 font-semibold"
            >
              {loading ? "Adding…" : "Add Staff"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
